import React from 'react';

import { Box, Button, Group, PasswordInput, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconMail, IconTags } from '@tabler/icons-react';
import 'react-international-phone/style.css';
import { useAuth } from 'context/auth-context';
import useSalesPortalCreateUser from 'hooks/salesPortal/useSalesPortalCreateUser';
import { SalesPortalUserCreateRequest } from 'types';
import { emailValidator, nameValidator } from 'utils/form-validators';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

interface FormValues {
  userId: string;
  email: string;
  firstName: string;
  surname: string;
  lastName: string;
  password: string;
}

interface ICreateAccountProps {
  email?: string;
  handleClose: () => void;
}

const CreateAccount = ({ email = '', handleClose }: ICreateAccountProps): JSX.Element => {
  const { refreshMe } = useAuth();
  const { successNotification, errorNotification } = notificationHandler();
  const createMutation = useSalesPortalCreateUser();

  const form = useForm<FormValues>({
    validateInputOnChange: true,
    initialValues: {
      userId: '',
      email: email,
      firstName: '',
      lastName: '',
      surname: '',
      password: '',
    },
    validate: {
      firstName: value => nameValidator(value),
      lastName: value => nameValidator(value),
      email: value => emailValidator(value),
      password: value => {
        if (!value) return 'Required';
        if (value.length < 8) return 'Password must be at least 8 characters';
        return null;
      },
    },

    transformValues: values => ({
      ...values,
    }),
  });

  const handleCloseAndReset = (): void => {
    handleClose();
    form.reset();
  };

  const handleSubmit = (values: FormValues): void => {
    const payload: SalesPortalUserCreateRequest = {
      ...values,
      userId: uuidv4(),
    };
    createMutation.mutate(payload, {
      onError: error => {
        errorNotification('Account creation failed', error.response?.data.message);
      },
      onSuccess: () => {
        successNotification('Account created successfully');
        handleCloseAndReset();
        refreshMe();
      },
    });
  };

  return (
    <Box px='md'>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='xs'>
          <TextInput
            label='First Name'
            placeholder='Enter First Name'
            required
            icon={<IconTags />}
            {...form.getInputProps('firstName')}
          />
          <TextInput
            label='Last Name'
            placeholder='Enter Last Name'
            required
            icon={<IconTags />}
            {...form.getInputProps('lastName')}
          />
          <TextInput mt='md' type='hidden' {...form.getInputProps('surname')} />
          <TextInput
            type='email'
            label='Email'
            placeholder='Enter Email'
            required
            icon={<IconMail />}
            disabled
            {...form.getInputProps('email')}
          />
          <PasswordInput
            label='Password'
            placeholder='•••••••'
            required
            {...form.getInputProps('password')}
            error={form.errors.password}
          />
          <Group position='right'>
            <Button variant='subtle' onClick={handleCloseAndReset} disabled={createMutation.isLoading}>
              Cancel
            </Button>
            <Button loading={createMutation.isLoading} type='submit'>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    </Box>
  );
};

export default CreateAccount;
